import { commonShortFieldLabels, commonShortNouns } from "./common-short-fields";
import { platformAdminExecutionControlPlane } from "./platform-admin-execution-control-plane";
import { platformAdminExecutionEvents } from "./platform-admin-execution-events";
import { platformAdminExecutionRuntimeValues } from "./platform-admin-execution-runtime-values";

export const platformAdminExecution = {
  title: "执行底座",
  subtitle: "统一查看执行状态与治理动作。",
  directRead: "当前页面已直接读取 execution-fabric 的执行主数据与任务接口。",
  indirectRead: "当前页面已收口为执行底座直连模式。请使用统一身份登录后查看执行控制面数据。",
  summaryProvider: "执行提供方：{value}",
  summaryImages: "镜像：{count}",
  summaryInstances: "执行实例：{count}",
  summaryDocker: "容器实例：{count}",
  summaryTasks: "任务：{count}",
  summaryWorkers: "活跃执行器：{count}",
  summaryPendingOldest: "最老待处理：{count} 秒",
  summaryLeaseRisk: "租约风险：{count}",
  summaryControlPlaneState: "控制面状态：{value}",
  summaryCriticalAlerts: "严重告警：{count}",
  summaryWarningAlerts: "预警告警：{count}",
  summaryWarningActions: "预警动作：{count}",
  summarySaturatedActions: "拥塞动作：{count}",
  summaryCapabilityBlockedActions: "能力阻塞动作：{count}",
  summaryClaimConflicted: "Claim 冲突：{count}",
  summaryRateLimited: "限流阻塞：{count}",
  summaryCapabilityMismatched: "能力不匹配：{count}",
  summaryDispatchFailed: "派发失败：{count}",
  summaryCoveredWorkerPolicies: "已覆盖 worker 策略：{count}",
  summaryMissingWorkerPolicies: "缺失 worker 策略：{count}",
  summaryAgeWarningActions: "老化预警动作：{count}",
  summaryRunning: "运行中：{count}",
  summaryFailed: "失败：{count}",
  summaryRetryableFailed: "可重试失败：{count}",
  summaryAutoRetryScheduled: "待自动重试：{count}",
  legend: {
    provider: "执行提供方",
    images: "镜像",
    instances: "执行实例",
    docker: "容器实例",
    tasks: "任务",
    workers: "活跃执行器",
    pendingOldest: "最老待处理",
    leaseRisk: "租约风险",
    controlPlaneState: "控制面状态",
    criticalAlerts: "严重告警",
    warningAlerts: "预警告警",
    warningActions: "预警动作",
    saturatedActions: "拥塞动作",
    capabilityBlockedActions: "能力阻塞动作",
    claimConflicted: "Claim 冲突",
    rateLimited: "限流阻塞",
    capabilityMismatched: "能力不匹配",
    dispatchFailed: "派发失败",
    coveredWorkerPolicies: "已覆盖 worker 策略",
    missingWorkerPolicies: "缺失 worker 策略",
    ageWarningActions: "老化预警动作",
    running: "运行中",
    failed: "失败",
    retryableFailed: "可重试失败",
    autoRetryScheduled: "待自动重试",
  },
  providerLabel: "当前执行提供方：{value}",
  centerBaseUrl: "执行底座地址：{value}",
  openCenter: "打开执行底座",
  openExamExecution: "打开考试执行页",
  cards: {
    access: "接入信息",
    controlPlane: "控制面概览",
  },
  ...platformAdminExecutionControlPlane,
  tabs: {
    images: "镜像 ({count})",
    instances: "执行实例 ({count})",
    docker: "容器实例 ({count})",
    tasks: "执行任务 ({count})",
    events: "运行事件",
    rawEvents: "原始事件",
    errorCodes: "错误码目录 ({count})",
  },
  columns: {
    instance: "实例 ID",
    containerId: "容器 ID",
    businessApp: "服务代号 / 来源应用",
    businessObject: "业务对象",
    instanceKind: "实例类型",
    runtimeType: "运行类型",
    question: "题目",
    status: commonShortNouns.status,
    type: "类型",
    accessMethods: "访问方式",
    primaryAccessEndpoint: "主访问入口",
    accessEndpoint: "访问入口",
    createdAt: commonShortFieldLabels.createdAt,
    expiresAt: "过期时间",
    destroyedAt: "销毁时间",
    image: "镜像",
    hostPort: "宿主端口",
    containerPort: "容器端口",
    source: "来源",
    size: "大小",
    defaultPort: "默认端口",
    tags: "标签",
    container: "容器",
    updatedAt: commonShortFieldLabels.updatedAt,
    keyword: "关键词",
    retryableOnly: "仅可重试",
    task: "任务",
    action: "动作",
    operator: "操作者",
    eventConclusion: "结论",
    requestId: "幂等键",
    trace: "链路追踪",
    retryFrom: "重试自",
    retryState: "重试状态",
    failureCode: "失败码",
    autoRetry: "自动重试",
    error: "错误",
    actions: "操作",
    resourceType: "资源类型",
    eventStatus: "事件状态",
    eventCategory: "事件类别",
    event: "事件",
    resource: "资源",
    eventTime: "事件时间",
    code: "错误码",
    name: commonShortFieldLabels.name,
    category: "分类",
    stage: "阶段",
    retryable: "可重试",
    description: "说明",
  },
  values: {
    enabled: "启用",
    disabled: "停用",
    undefinedRuntimeTargetStatus: "未定义运行目标状态",
    yes: "是",
    no: "否",
    ...platformAdminExecutionRuntimeValues.values,
    retryable: platformAdminExecutionEvents.values.retryable,
    retried: platformAdminExecutionEvents.values.retried,
    succeeded: platformAdminExecutionEvents.values.succeeded,
    exhausted: platformAdminExecutionEvents.values.exhausted,
    retryScheduled: platformAdminExecutionEvents.values.retryScheduled,
    scheduled: platformAdminExecutionEvents.values.scheduled,
    processing: platformAdminExecutionEvents.values.processing,
    dispatchRetried: platformAdminExecutionEvents.values.dispatchRetried,
    dispatchFailed: platformAdminExecutionEvents.values.dispatchFailed,
    deadLettered: platformAdminExecutionEvents.values.deadLettered,
    manualInterventionRequired: platformAdminExecutionEvents.values.manualInterventionRequired,
    autoRetryExhausted: platformAdminExecutionEvents.values.autoRetryExhausted,
    pending: platformAdminExecutionEvents.values.pending,
    running: platformAdminExecutionEvents.values.running,
    success: platformAdminExecutionEvents.values.success,
    failed: platformAdminExecutionEvents.values.failed,
    cancelled: platformAdminExecutionEvents.values.cancelled,
    eventTaskPending: platformAdminExecutionEvents.values.eventTaskPending,
    eventTaskClaimed: platformAdminExecutionEvents.values.eventTaskClaimed,
    eventTaskClaimReleased: platformAdminExecutionEvents.values.eventTaskClaimReleased,
    eventTaskRunning: platformAdminExecutionEvents.values.eventTaskRunning,
    eventTaskSucceeded: platformAdminExecutionEvents.values.eventTaskSucceeded,
    eventTaskFailed: platformAdminExecutionEvents.values.eventTaskFailed,
    eventTaskCancelled: platformAdminExecutionEvents.values.eventTaskCancelled,
    eventTaskCancellationRequested:
      platformAdminExecutionEvents.values.eventTaskCancellationRequested,
    eventTaskLeaseRenewed: platformAdminExecutionEvents.values.eventTaskLeaseRenewed,
    eventTaskClaimConflicted: platformAdminExecutionEvents.values.eventTaskClaimConflicted,
    eventTaskDispatchCapabilityMismatch:
      platformAdminExecutionEvents.values.eventTaskDispatchCapabilityMismatch,
    eventTaskDispatchRateLimited: platformAdminExecutionEvents.values.eventTaskDispatchRateLimited,
    eventTaskAutoRetryDispatchFailed:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDispatchFailed,
    eventTaskAutoRetryDeadLettered:
      platformAdminExecutionEvents.values.eventTaskAutoRetryDeadLettered,
    eventTaskAutoRetryManualInterventionRequired:
      platformAdminExecutionEvents.values.eventTaskAutoRetryManualInterventionRequired,
    eventTaskStaleRunningDetected:
      platformAdminExecutionEvents.values.eventTaskStaleRunningDetected,
    eventStatusRecorded: platformAdminExecutionEvents.values.eventStatusRecorded,
    eventCategoryTask: platformAdminExecutionEvents.values.eventCategoryTask,
    eventCategoryDispatch: platformAdminExecutionEvents.values.eventCategoryDispatch,
    eventCategoryLease: platformAdminExecutionEvents.values.eventCategoryLease,
    eventCategoryRecovery: platformAdminExecutionEvents.values.eventCategoryRecovery,
    eventCategoryOther: platformAdminExecutionEvents.values.eventCategoryOther,
    resourceExecutionTask: platformAdminExecutionEvents.values.resourceExecutionTask,
    resourceEnvironmentInstance: platformAdminExecutionEvents.values.resourceEnvironmentInstance,
    resourceExecutionTaskWithId: platformAdminExecutionEvents.values.resourceExecutionTaskWithId,
    resourceEnvironmentInstanceWithId:
      platformAdminExecutionEvents.values.resourceEnvironmentInstanceWithId,
    eventConclusionSuccess: platformAdminExecutionEvents.values.eventConclusionSuccess,
    eventConclusionFailed: platformAdminExecutionEvents.values.eventConclusionFailed,
    eventConclusionFailedWithReason:
      platformAdminExecutionEvents.values.eventConclusionFailedWithReason,
    eventConclusionRetryState: platformAdminExecutionEvents.values.eventConclusionRetryState,
  },
  legendSemantic: {
    all: "全部",
    total: "总数",
    online: "在线",
    offline: "离线",
    unknown: "未知",
    enabled: "已启用",
    disabled: "未启用",
    running: "运行中",
    failed: "失败",
    destroyed: "已销毁",
    warning: "告警",
    draft: "草稿",
    healthy: "健康",
    default: "默认",
    active: "活跃",
    current: "当前",
    retry: "重试",
    remaining: "剩余",
    pending: "处理中",
    succeeded: "成功",
    published: "已发布",
    archived: "已归档",
    public: "公开",
    private: "私密",
    shared: "共享",
    mine: "我的",
    used: "已使用",
    unused: "未使用",
    visible: "可见",
    hidden: "隐藏",
    inactive: "未激活",
    degraded: "降级",
    mvp: "基础",
    reserved: "预留",
    container: "容器",
    vm: "虚拟机",
    expired: "已过期",
    console: "控制台",
    recycles: "回收任务",
    operator: "操作者",
    create: "创建",
    update: "更新",
    delete: "删除",
    template: "模板",
  },
  actions: {
    view: "查看",
    retry: platformAdminExecutionEvents.actions.retry,
    retryTask: platformAdminExecutionEvents.actions.retryTask,
    processAutoRetries: platformAdminExecutionEvents.actions.processAutoRetries,
  },
  drawer: {
    title: platformAdminExecutionEvents.drawer.title,
    titleShort: platformAdminExecutionEvents.drawer.titleShort,
    eventTitle: platformAdminExecutionEvents.drawer.eventTitle,
    eventTitleShort: platformAdminExecutionEvents.drawer.eventTitleShort,
    instanceTitle: "实例 #{id}",
    dockerInstanceTitle: "运行时容器 · {id}",
    rawEventTitle: platformAdminExecutionEvents.drawer.rawEventTitle,
    rawEventTitleShort: platformAdminExecutionEvents.drawer.rawEventTitleShort,
    requestPayload: platformAdminExecutionEvents.drawer.requestPayload,
    resultPayload: platformAdminExecutionEvents.drawer.resultPayload,
  },
  fields: {
    appName: "业务显示名称",
    appKey: "服务代号",
    businessObject: "业务对象",
    instanceKind: "实例类型",
    runtimeType: "运行类型",
    providerType: "运行提供方",
    providerKey: "提供方标识",
    nodeKey: "运行节点",
    targetKey: "运行目标",
    providerRef: "运行引用",
    taskId: platformAdminExecutionEvents.fields.taskId,
    tenantId: "租户 ID",
    organizationId: "组织 ID",
    examId: "考试 ID",
    questionId: "题目 ID",
    attemptId: "作答 ID",
    attemptQuestionId: "作答题目 ID",
    candidateId: "考生 ID",
    sourceType: "来源类型",
    objectType: "业务对象类型",
    questionTitle: "题目标题",
    displayOrder: "题目序号",
    questionType: "业务题型",
    environmentType: "环境类型",
    eventAction: platformAdminExecutionEvents.fields.eventAction,
    eventTrigger: platformAdminExecutionEvents.fields.eventTrigger,
    workerId: platformAdminExecutionEvents.fields.workerId,
    requiredCapability: platformAdminExecutionEvents.fields.requiredCapability,
    dispatchDecision: platformAdminExecutionEvents.fields.dispatchDecision,
    leaseOwner: platformAdminExecutionEvents.fields.leaseOwner,
    leaseExpiresAt: platformAdminExecutionEvents.fields.leaseExpiresAt,
    retrySource: platformAdminExecutionEvents.fields.retrySource,
    rootTask: platformAdminExecutionEvents.fields.rootTask,
    retryDepth: platformAdminExecutionEvents.fields.retryDepth,
    retryCount: platformAdminExecutionEvents.fields.retryCount,
    autoRetryEnabled: platformAdminExecutionEvents.fields.autoRetryEnabled,
    autoRetryState: platformAdminExecutionEvents.fields.autoRetryState,
    autoRetryMax: platformAdminExecutionEvents.fields.autoRetryMax,
    autoRetryDelay: platformAdminExecutionEvents.fields.autoRetryDelay,
    nextRetryAt: platformAdminExecutionEvents.fields.nextRetryAt,
    latestRetry: platformAdminExecutionEvents.fields.latestRetry,
    startTime: platformAdminExecutionEvents.fields.startTime,
    finishTime: platformAdminExecutionEvents.fields.finishTime,
  },
  contract: {
    missingServiceIdentity: "缺少 source_app/app_key",
  },
  columnHelp: {
    event: platformAdminExecutionEvents.columnHelp.event,
    resource: platformAdminExecutionEvents.columnHelp.resource,
    status: platformAdminExecutionEvents.columnHelp.status,
    eventConclusion: platformAdminExecutionEvents.columnHelp.eventConclusion,
    eventCategory: platformAdminExecutionEvents.columnHelp.eventCategory,
  },
  messages: {
    taskDetailLoadFailed: platformAdminExecutionEvents.messages.taskDetailLoadFailed,
    taskRetryStarted: platformAdminExecutionEvents.messages.taskRetryStarted,
    taskRetryFailed: platformAdminExecutionEvents.messages.taskRetryFailed,
    autoRetryProcessed: platformAdminExecutionEvents.messages.autoRetryProcessed,
    autoRetryProcessFailed: platformAdminExecutionEvents.messages.autoRetryProcessFailed,
    errorCodesEmpty:
      "当前错误码目录为空。这通常表示执行底座读取失败、直连状态异常，或页面数据未正确加载。",
  },
  units: {
    seconds: "{value} 秒",
    workersShort: "执行器",
  },
} as const;
